// ---------------------------------------------------------------------------
// ChannelCardHeader — avatar + name + platform icon + scraping toggle.
// ---------------------------------------------------------------------------

import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import type { PlatformId } from '@/components/v11/discovery/types';
import type { ChannelCardHeaderProps } from './types';

const AVATAR_BG_COLORS: Readonly<Record<string, string>> = {
  instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
  x: 'bg-gray-700',
  tiktok: 'bg-gray-900',
  youtube: 'bg-red-600',
};

export function ChannelCardHeader({
  channel,
  period,
}: ChannelCardHeaderProps) {
  const platformKey = channel.platform.toLowerCase() as PlatformId;
  const avatarFallbackBg = AVATAR_BG_COLORS[platformKey] ?? 'bg-gray-600';
  const title =
    channel.fullName && channel.fullName.trim().length > 0 ? channel.fullName : channel.username;
  const initial = (title?.[0] ?? '?').toUpperCase();

  const platformIcon = getPlatformIcon(channel.platform);

  const periodParam = period && period !== '30d' ? `&period=${period}` : '';
  const discoveryUrl = `/v11/discovery?f_channel=${channel.authorId}&f_channel_labels=${encodeURIComponent(channel.username)}&f_channel_platforms=${encodeURIComponent(channel.platform)}${periodParam}`;

  return (
    <div className="p-4 pb-3 flex items-start gap-3">
      {/* Avatar */}
      <Link to={discoveryUrl} title="View in Discovery" className="flex-shrink-0">
        {channel.profilePicUrl ? (
          <img
            src={channel.profilePicUrl}
            alt={channel.username}
            className="h-11 w-11 rounded-full object-cover hover:ring-2 hover:ring-indigo-500/50 transition-all"
            onError={e => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement | null;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`h-11 w-11 rounded-full ${avatarFallbackBg} items-center justify-center text-white font-bold text-lg hover:ring-2 hover:ring-indigo-500/50 transition-all`}
          style={{ display: channel.profilePicUrl ? 'none' : 'flex' }}
        >
          {initial}
        </div>
      </Link>

      {/* Name + handle */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <Link
            to={discoveryUrl}
            className="font-semibold text-v11-text-primary truncate text-sm hover:text-indigo-400 transition-colors inline-flex items-center gap-1.5"
            title="View in Discovery"
          >
            {title}
            <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-50" />
          </Link>
          {platformIcon && (
            <span
              className="flex-shrink-0 inline-flex items-center"
              style={{ color: platformIcon.color }}
              title={platformIcon.name}
              aria-label={platformIcon.name}
            >
              {platformIcon.icon}
            </span>
          )}
        </div>
        <p className="text-xs text-v11-text-muted truncate mt-0.5">
          @{channel.username} · {channel.platform}
        </p>
      </div>
    </div>
  );
}
